import { useEffect, useState } from "react";
import { Navigate, Route, Routes, Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Layers, ShoppingCart, LogOut, Plus, Pencil, Trash2,
  Upload, X, ArrowLeft, Save, Star, Users, FileText,
} from "lucide-react";
import { api, fmtUSD, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminContent from "@/pages/AdminContent";

/* ================= Layout ================= */

function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const item = "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors";
  const activeCls = "bg-[#173a7a] text-white";
  const idle = "text-slate-600 hover:bg-slate-100";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-shell">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-5 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5">
            <img src="/products/img_10.png" alt="Morning Soft" className="h-9 w-9 object-contain" />
            <span className="font-display text-lg font-extrabold text-[#173a7a]">
              Morning<span className="text-amber-500">Soft</span>
              <span className="text-slate-400 text-sm ml-2 font-normal">Admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1" data-testid="admin-nav">
          {[
            { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
            { to: "/admin/content", label: "Content", icon: FileText },
            { to: "/admin/products", label: "Products", icon: Package },
            { to: "/admin/categories", label: "Categories", icon: Layers },
            { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
          ].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `${item} ${isActive ? activeCls : idle}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
          <Link to="/" className={`${item} ${idle} mt-8 border-t border-slate-100 pt-4`}>
            <ArrowLeft size={16} /> Back to storefront
          </Link>
        </aside>

        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
}

/* ================= Guard ================= */

export function AdminGuard({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin" }} />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

/* ================= Dashboard ================= */

function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const tiles = stats ? [
    { label: "Products", value: stats.products, icon: Package, hue: "bg-sky-50 text-[#173a7a]" },
    { label: "Categories", value: stats.categories, icon: Layers, hue: "bg-amber-50 text-amber-600" },
    { label: "Customers", value: stats.customers, icon: Users, hue: "bg-emerald-50 text-emerald-600" },
    { label: "Paid Orders", value: stats.orders_paid, icon: ShoppingCart, hue: "bg-fuchsia-50 text-fuchsia-600" },
    { label: "Reviews", value: stats.reviews, icon: Star, hue: "bg-orange-50 text-orange-600" },
    { label: "Revenue", value: fmtUSD(stats.revenue), icon: LayoutDashboard, hue: "bg-slate-100 text-slate-700" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">Overview</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">Dashboard</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((t, i) => (
          <motion.div key={t.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="rounded-2xl bg-white border border-slate-100 p-5" data-testid={`stat-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`w-9 h-9 rounded-full ${t.hue} flex items-center justify-center`}><t.icon size={16} /></div>
            <p className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-bold">{t.label}</p>
            <p className="mt-1 font-display text-2xl font-black text-slate-900">{t.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-6">
        <p className="font-display font-bold text-lg text-[#0f2a5c]">Quick actions</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/admin/products/new" data-testid="quick-new-product"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#173a7a] text-white text-sm font-semibold">
            <Plus size={14} /> New product
          </Link>
          <Link to="/admin/categories" data-testid="quick-manage-categories"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold">
            <Layers size={14} /> Manage categories
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ================= Products List ================= */

function ProductsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => api.get("/admin/products").then(({ data }) => setRows(data.products)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete product "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${slug}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">Catalog</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">Products</h1>
        </div>
        <button onClick={() => nav("/admin/products/new")} data-testid="admin-new-product-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#173a7a] text-white text-sm font-semibold">
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="mt-8 rounded-2xl bg-white border border-slate-100 overflow-hidden">
        {loading ? (
          <p className="p-6 text-slate-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-slate-400" data-testid="admin-products-empty">No products.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-bold">Product</th>
                <th className="px-5 py-3 font-bold hidden md:table-cell">Category</th>
                <th className="px-5 py-3 font-bold">Price</th>
                <th className="px-5 py-3 font-bold hidden md:table-cell">Featured</th>
                <th className="px-5 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-products-table">
              {rows.map((p) => (
                <tr key={p.slug} className="border-t border-slate-100" data-testid={`admin-product-row-${p.slug}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={(p.images || [])[0] || "/products/img_10.png"} alt=""
                        className="w-11 h-11 rounded-lg bg-sky-50 object-contain p-1" />
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                        <p className="text-xs text-slate-400 truncate">/{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-slate-600">{p.category}</td>
                  <td className="px-5 py-3 font-semibold">{fmtUSD(p.price)}</td>
                  <td className="px-5 py-3 hidden md:table-cell">{p.featured ? "★" : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/admin/products/${p.slug}`} data-testid={`admin-edit-${p.slug}`}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil size={14} /></Link>
                      <button onClick={() => remove(p.slug)} data-testid={`admin-delete-${p.slug}`}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ================= Product Editor ================= */

function ProductEditor() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === "new";
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    slug: "", name: "", category: "", price: 0, compare_at: null,
    tagline: "", short: "", description: "",
    features: [], specs: {}, images: [], featured: false, badge: "", active: true,
  });
  const [featureText, setFeatureText] = useState("");
  const [specKey, setSpecKey] = useState("");
  const [specVal, setSpecVal] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data.categories));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/products/${routeSlug}`).then(({ data }) => {
      setForm({
        slug: data.slug, name: data.name, category: data.category, price: data.price,
        compare_at: data.compare_at ?? null, tagline: data.tagline || "", short: data.short || "",
        description: data.description || "", features: data.features || [], specs: data.specs || {},
        images: data.images || [], featured: !!data.featured, badge: data.badge || "", active: data.active !== false,
      });
    });
  }, [isNew, routeSlug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const MAX_IMAGES = 5;

  const uploadFile = async (file, replaceIndex = null) => {
    if (!file) return;
    if (replaceIndex === null && form.images.length >= MAX_IMAGES) {
      toast.error(`You can have up to ${MAX_IMAGES} images per product.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (replaceIndex !== null) {
        const next = [...form.images];
        next[replaceIndex] = data.url;
        set("images", next);
        toast.success("Image replaced");
      } else {
        set("images", [...form.images, data.url]);
        toast.success("Image uploaded");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const addImageByUrl = (replaceIndex = null) => {
    if (replaceIndex === null && form.images.length >= MAX_IMAGES) {
      toast.error(`You can have up to ${MAX_IMAGES} images per product.`);
      return;
    }
    const url = window.prompt("Image URL (e.g. /products/img_01.png or /api/files/x.png):");
    if (!url) return;
    if (replaceIndex !== null) {
      const next = [...form.images];
      next[replaceIndex] = url;
      set("images", next);
    } else {
      set("images", [...form.images, url]);
    }
  };

  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const moveImage = (from, to) => {
    if (to < 0 || to >= form.images.length) return;
    const next = [...form.images];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    set("images", next);
  };

  const addFeature = () => {
    if (featureText.trim()) {
      set("features", [...form.features, featureText.trim()]);
      setFeatureText("");
    }
  };

  const removeFeature = (i) => set("features", form.features.filter((_, idx) => idx !== i));

  const addSpec = () => {
    if (specKey.trim() && specVal.trim()) {
      set("specs", { ...form.specs, [specKey.trim()]: specVal.trim() });
      setSpecKey(""); setSpecVal("");
    }
  };

  const removeSpec = (k) => {
    const s = { ...form.specs }; delete s[k]; set("specs", s);
  };

  const save = async () => {
    if (!form.slug || !form.name || !form.category || !form.price) {
      toast.error("Slug, name, category, and price are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        compare_at: form.compare_at ? Number(form.compare_at) : null,
      };
      if (isNew) {
        await api.post("/admin/products", payload);
      } else {
        await api.put(`/admin/products/${routeSlug}`, payload);
      }
      toast.success("Saved");
      nav("/admin/products");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-product-editor">
      <Link to="/admin/products" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#173a7a] mb-4">
        <ArrowLeft size={14} /> Back to products
      </Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">{isNew ? "New" : "Editing"}</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">
            {isNew ? "Create product" : form.name || routeSlug}
          </h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-save-product"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#173a7a] text-white text-sm font-semibold disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save product"}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main fields */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
            <FieldRow label="Slug" required>
              <input data-testid="editor-slug" value={form.slug} onChange={(e) => set("slug", e.target.value)}
                placeholder="e.g. bathroom-tissue-36-rolls"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
            </FieldRow>
            <FieldRow label="Name" required>
              <input data-testid="editor-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
            </FieldRow>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FieldRow label="Category" required>
                <select data-testid="editor-category" value={form.category} onChange={(e) => set("category", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a] bg-white">
                  <option value="">Select…</option>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Price (USD)" required>
                <input data-testid="editor-price" type="number" step="0.01" min="0"
                  value={form.price} onChange={(e) => set("price", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
              </FieldRow>
              <FieldRow label="Compare at (optional)">
                <input data-testid="editor-compare-at" type="number" step="0.01" min="0"
                  value={form.compare_at ?? ""} onChange={(e) => set("compare_at", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
              </FieldRow>
            </div>
            <FieldRow label="Tagline">
              <input data-testid="editor-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
            </FieldRow>
            <FieldRow label="Short description">
              <input data-testid="editor-short" value={form.short} onChange={(e) => set("short", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
            </FieldRow>
            <FieldRow label="Full description">
              <textarea data-testid="editor-description" value={form.description} onChange={(e) => set("description", e.target.value)}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a] resize-none" />
            </FieldRow>
          </div>

          {/* Features */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6">
            <p className="font-display font-bold text-lg text-[#0f2a5c]">Features</p>
            <ul className="mt-4 space-y-2" data-testid="editor-features-list">
              {form.features.map((f, i) => (
                <li key={i} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50 text-sm">
                  <span>{f}</span>
                  <button onClick={() => removeFeature(i)} data-testid={`remove-feature-${i}`} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                </li>
              ))}
              {form.features.length === 0 && <li className="text-sm text-slate-400">No features yet.</li>}
            </ul>
            <div className="mt-3 flex gap-2">
              <input data-testid="editor-new-feature" value={featureText} onChange={(e) => setFeatureText(e.target.value)}
                placeholder="Add a feature bullet"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
              <button onClick={addFeature} data-testid="editor-add-feature"
                className="px-4 py-3 rounded-xl bg-[#173a7a] text-white text-sm font-semibold">Add</button>
            </div>
          </div>

          {/* Specs */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6">
            <p className="font-display font-bold text-lg text-[#0f2a5c]">Specifications</p>
            <ul className="mt-4 space-y-2" data-testid="editor-specs-list">
              {Object.entries(form.specs).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50 text-sm">
                  <span><b>{k}</b>: {v}</span>
                  <button onClick={() => removeSpec(k)} data-testid={`remove-spec-${k}`} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                </li>
              ))}
              {Object.keys(form.specs).length === 0 && <li className="text-sm text-slate-400">No specs yet.</li>}
            </ul>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <input data-testid="editor-spec-key" value={specKey} onChange={(e) => setSpecKey(e.target.value)} placeholder="Key (e.g. Sheets)"
                className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
              <input data-testid="editor-spec-val" value={specVal} onChange={(e) => setSpecVal(e.target.value)} placeholder="Value (e.g. 320)"
                className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
              <button onClick={addSpec} data-testid="editor-add-spec"
                className="px-4 py-3 rounded-xl bg-[#173a7a] text-white text-sm font-semibold">Add</button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="rounded-2xl bg-white border border-slate-100 p-6">
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-lg text-[#0f2a5c]">Images</p>
              <span className="text-xs font-semibold text-slate-500" data-testid="editor-image-counter">
                {form.images.length} / {MAX_IMAGES}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">Up to 5 images. First image is the primary. Hover a slot to replace or remove.</p>

            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="editor-images-grid">
              {Array.from({ length: MAX_IMAGES }).map((_, i) => {
                const img = form.images[i];
                const isPrimary = i === 0 && !!img;
                return (
                  <div key={i} className="relative group aspect-square rounded-xl bg-sky-50 border border-slate-100 overflow-hidden" data-testid={`editor-slot-${i}`}>
                    {img ? (
                      <>
                        <img src={img} alt="" className="w-full h-full object-contain p-1" />
                        {isPrimary && (
                          <span className="absolute bottom-1 left-1 rounded-full bg-[#173a7a] text-white text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5">
                            Primary
                          </span>
                        )}
                        {/* Hover controls */}
                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                          <label className="cursor-pointer inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white text-[#173a7a] text-[11px] font-semibold" data-testid={`editor-replace-label-${i}`}>
                            <Upload size={11} /> Replace
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              data-testid={`editor-replace-input-${i}`}
                              onChange={(e) => uploadFile(e.target.files?.[0], i)}
                            />
                          </label>
                          <div className="flex items-center gap-1">
                            {i > 0 && (
                              <button
                                type="button"
                                onClick={() => moveImage(i, i - 1)}
                                data-testid={`editor-image-move-up-${i}`}
                                className="px-2 py-1 rounded-full bg-white/90 text-slate-700 text-[10px] font-semibold"
                              >↑</button>
                            )}
                            {i < form.images.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveImage(i, i + 1)}
                                data-testid={`editor-image-move-down-${i}`}
                                className="px-2 py-1 rounded-full bg-white/90 text-slate-700 text-[10px] font-semibold"
                              >↓</button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              data-testid={`editor-remove-image-${i}`}
                              className="p-1.5 rounded-full bg-white/90 text-red-500"
                            ><X size={12} /></button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <label className="w-full h-full flex flex-col items-center justify-center gap-1 cursor-pointer text-slate-400 hover:text-[#173a7a] hover:bg-sky-50/70 transition-colors" data-testid={`editor-empty-slot-${i}`}>
                        <Upload size={16} />
                        <span className="text-[10px] font-semibold uppercase tracking-widest">Slot {i + 1}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid={`editor-empty-slot-input-${i}`}
                          onChange={(e) => uploadFile(e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <label className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed text-sm transition-colors ${
                form.images.length >= MAX_IMAGES
                  ? "border-slate-100 text-slate-300 cursor-not-allowed"
                  : "border-slate-200 text-slate-600 cursor-pointer hover:border-[#173a7a]"
              }`}
                data-testid="editor-upload-label">
                <Upload size={14} /> {uploading ? "Uploading…" : form.images.length >= MAX_IMAGES ? "Maximum 5 images reached" : "Upload image"}
                <input data-testid="editor-upload-input" type="file" accept="image/*" className="hidden"
                  disabled={form.images.length >= MAX_IMAGES}
                  onChange={(e) => uploadFile(e.target.files?.[0])} />
              </label>
              <button onClick={() => addImageByUrl(null)} data-testid="editor-add-image-url"
                disabled={form.images.length >= MAX_IMAGES}
                className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                Add by URL
              </button>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="editor-featured">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)}
                className="w-4 h-4 accent-[#173a7a]" />
              <span className="text-sm font-semibold">Featured on homepage</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="editor-active">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
                className="w-4 h-4 accent-[#173a7a]" />
              <span className="text-sm font-semibold">Active (visible in shop)</span>
            </label>
            <FieldRow label="Badge (optional)">
              <input data-testid="editor-badge" value={form.badge || ""} onChange={(e) => set("badge", e.target.value)}
                placeholder="e.g. Best Seller"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
            </FieldRow>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ================= Categories ================= */

function CategoriesPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "" });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/categories").then(({ data }) => setRows(data.categories));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.name) return;
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing}`, form);
      } else {
        await api.post("/admin/categories", form);
      }
      toast.success(editing ? "Category updated" : "Category added");
      setForm({ slug: "", name: "" }); setEditing(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"?`)) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">Taxonomy</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">Categories</h1>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <form onSubmit={save} className="rounded-2xl bg-white border border-slate-100 p-6 space-y-3" data-testid="cat-form">
          <p className="font-display font-bold text-[#0f2a5c]">{editing ? "Edit category" : "New category"}</p>
          <FieldRow label="Slug" required>
            <input data-testid="cat-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="e.g. baby-care"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
          </FieldRow>
          <FieldRow label="Name" required>
            <input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Baby Care"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />
          </FieldRow>
          <div className="flex gap-2">
            <button data-testid="cat-save" className="flex-1 px-4 py-3 rounded-full bg-[#173a7a] text-white text-sm font-semibold">
              {editing ? "Save changes" : "Add category"}
            </button>
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm({ slug: "", name: "" }); }}
                className="px-4 py-3 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">Cancel</button>
            )}
          </div>
        </form>

        <div className="md:col-span-2 rounded-2xl bg-white border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs uppercase text-left tracking-wider text-slate-500">
                <th className="px-5 py-3">Slug</th><th className="px-5 py-3">Name</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="cat-list">
              {rows.map((c) => (
                <tr key={c.slug} className="border-t border-slate-100" data-testid={`cat-row-${c.slug}`}>
                  <td className="px-5 py-3 font-mono text-slate-500">{c.slug}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => { setEditing(c.slug); setForm({ slug: c.slug, name: c.name }); }}
                        data-testid={`cat-edit-${c.slug}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil size={14} /></button>
                      <button onClick={() => remove(c.slug)} data-testid={`cat-delete-${c.slug}`}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-slate-400 text-center">No categories.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ================= Orders ================= */

function OrdersPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/orders")
      .then(({ data }) => setRows(data.orders || []))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);
  return (
    <div data-testid="admin-orders-page">
      <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">Sales</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">Orders</h1>
      {error && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="orders-error">
          Failed to load orders: {error}
        </div>
      )}
      <div className="mt-8 rounded-2xl bg-white border border-slate-100 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase text-left tracking-wider text-slate-500">
              <th className="px-5 py-3">Session</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Items</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o, idx) => (
              <tr key={o.session_id || `order-${idx}`} className="border-t border-slate-100" data-testid={`order-row-${idx}`}>
                <td className="px-5 py-3 font-mono text-xs text-slate-500">
                  {o.session_id ? `${o.session_id.slice(0, 16)}…` : <span className="italic text-slate-300">no session</span>}
                </td>
                <td className="px-5 py-3">{o.customer_email || "—"}</td>
                <td className="px-5 py-3">{(o.items || []).reduce((n, i) => n + (i.quantity || 0), 0)}</td>
                <td className="px-5 py-3 font-semibold">{fmtUSD(o.total || 0)}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                    {o.payment_status || "pending"}
                  </span>
                </td>
                <td className="px-5 py-3 text-slate-500">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-slate-400 text-center" data-testid="orders-empty">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Root ================= */

export default function AdminApp() {
  return (
    <AdminGuard>
      <AdminShell>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<ProductsList />} />
          <Route path="products/new" element={<ProductEditor />} />
          <Route path="products/:slug" element={<ProductEditor />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="content" element={<AdminContent />} />
        </Routes>
      </AdminShell>
    </AdminGuard>
  );
}
